import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  TextField,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
  Switch,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
} from "@mui/material";
import {
  Person,
  Email,
  Lock,
  Save,
  Visibility,
  VisibilityOff,
  Notifications,
  Badge as BadgeIcon,
  VerifiedUser,
  Devices,
  Laptop,
  PhoneIphone,
  DeleteForever,
  LogoutOutlined,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import api from "../../api/axiosInstance";
import {
  selectCurrentUser,
  setCredentials,
  logoutUser,
} from "../../redux/slices/authSlice";
import PageHeader from "../../components/common/PageHeader";
import ProfilePhotoUploader from "../../components/common/ProfilePhotoUploader";
import PhoneNumberField from "../../components/common/PhoneNumberField";
import { getSessionLabel } from "../../utils/sessionLabel";

dayjs.extend(relativeTime);

const getNotificationDefaults = (prefs = {}) => ({
  email: {
    newShipment: prefs?.email?.newShipment ?? true,
    statusChange: prefs?.email?.statusChange ?? true,
    payment: prefs?.email?.payment ?? true,
    overdueAlert: prefs?.email?.overdueAlert ?? true,
    dailyDigest: prefs?.email?.dailyDigest ?? false,
  },
  inApp: {
    newShipment: prefs?.inApp?.newShipment ?? true,
    statusChange: prefs?.inApp?.statusChange ?? true,
    payment: prefs?.inApp?.payment ?? true,
    overdueAlert: prefs?.inApp?.overdueAlert ?? true,
    dailyDigest: prefs?.inApp?.dailyDigest ?? false,
  },
});

const CustomerProfile = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const { enqueueSnackbar } = useSnackbar();

  const [profile, setProfile] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    phone: user?.phone || "",
    tin: user?.tin || "",
    street: user?.address?.street || "",
    district: user?.address?.district || "",
    island: user?.address?.island || "Grand Cayman",
  });

  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPw, setShowPw] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [pwError, setPwError] = useState("");
  const [notificationPreferences, setNotificationPreferences] = useState(
    getNotificationDefaults(user?.notificationPreferences),
  );

  useEffect(() => {
    setNotificationPreferences(getNotificationDefaults(user?.notificationPreferences));
  }, [user]);

  const handleNotificationToggle = async (channel, key, checked) => {
    const previousPreferences = notificationPreferences;
    const nextPreferences = {
      ...previousPreferences,
      [channel]: {
        ...previousPreferences[channel],
        [key]: checked,
      },
    };

    setNotificationPreferences(nextPreferences);
    setSavingNotifications(true);

    try {
      const { data } = await api.patch(`/users/me`, {
        notificationPreferences: nextPreferences,
      });
      dispatch(setCredentials({ user: data.data.user }));
      enqueueSnackbar("Notification preferences updated", {
        variant: "success",
      });
    } catch (err) {
      setNotificationPreferences(previousPreferences);
      enqueueSnackbar(
        err.response?.data?.message ||
        "Failed to update notification preferences",
        { variant: "error" },
      );
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleProfileSave = async () => {
    setSavingProfile(true);
    try {
      const { data } = await api.patch(`/users/me`, {
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone,
        tin: profile.tin,
        address: {
          street: profile.street,
          district: profile.district,
          island: profile.island,
        },
        notificationPreferences,
      });
      dispatch(setCredentials({ user: data.data.user }));
      enqueueSnackbar("Profile updated successfully", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(
        err.response?.data?.message || "Failed to update profile",
        { variant: "error" },
      );
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordChange = async () => {
    setPwError("");
    if (passwords.newPassword !== passwords.confirmPassword) {
      setPwError("Passwords do not match");
      return;
    }
    if (passwords.newPassword.length < 8) {
      setPwError("Password must be at least 8 characters");
      return;
    }
    setSavingPw(true);
    try {
      await api.patch("/auth/change-password", {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      enqueueSnackbar("Password changed successfully", { variant: "success" });
      setPasswords({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      setPwError(err.response?.data?.message || "Failed to change password");
    } finally {
      setSavingPw(false);
    }
  };

  // Submitted as a REQUEST, not an immediate delete — an admin reviews and
  // actually carries it out, since shipment/invoice/customs records can
  // carry retention requirements an instant delete would violate. Mirrors
  // the mobile app's identical flow (ProfileScreen.jsx).
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [requestingDeletion, setRequestingDeletion] = useState(false);
  const handleRequestDeletion = async () => {
    setRequestingDeletion(true);
    try {
      await api.post("/users/me/request-deletion");
      setDeleteConfirmOpen(false);
      enqueueSnackbar("Deletion request submitted — our team will review it shortly. You'll be signed out now.", {
        variant: "success",
      });
      setTimeout(() => dispatch(logoutUser()), 1500);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Couldn't submit request", { variant: "error" });
    } finally {
      setRequestingDeletion(false);
    }
  };

  // Active Sessions — every device currently logged into this account.
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [revokingId, setRevokingId] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);
  const [signOutAllConfirmOpen, setSignOutAllConfirmOpen] = useState(false);

  const loadSessions = () => {
    setSessionsLoading(true);
    api
      .get("/users/me/sessions")
      .then(({ data }) => setSessions(data.data.sessions || []))
      .catch(() => {})
      .finally(() => setSessionsLoading(false));
  };
  useEffect(loadSessions, []);

  const handleRevokeSession = async (session) => {
    setRevokingId(session.sessionId);
    try {
      await api.delete(`/users/me/sessions/${session.sessionId}`);
      if (session.isCurrent) {
        // Signing out this device is exactly a normal logout — the local
        // token needs clearing immediately rather than waiting to notice
        // the session is gone on some later request.
        dispatch(logoutUser());
        return;
      }
      setSessions((prev) => prev.filter((s) => s.sessionId !== session.sessionId));
      enqueueSnackbar("Signed out of that device", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Couldn't sign out that device", { variant: "error" });
    } finally {
      setRevokingId(null);
    }
  };

  const handleSignOutAll = async () => {
    setRevokingAll(true);
    try {
      await api.delete("/users/me/sessions");
      setSignOutAllConfirmOpen(false);
      dispatch(logoutUser());
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Couldn't sign out all devices", { variant: "error" });
      setRevokingAll(false);
    }
  };

  const initials = `${user?.firstName?.[0] || ""}${user?.lastName?.[0] || ""}`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="My Profile"
        subtitle="Manage your account details and preferences"
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: 3,
        }}
      >
        {/* Profile Info */}
        <Card>
          <CardHeader
            title={
              <Typography fontWeight={700} fontSize={14}>
                Personal Information
              </Typography>
            }
            avatar={<Person sx={{ color: "text.secondary" }} />}
            sx={{ pb: 1 }}
          />
          <CardContent sx={{ pt: 0 }}>
            {/* Avatar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 3,
                p: 2,
                bgcolor: "action.hover",
                borderRadius: 2,
              }}
            >
              <ProfilePhotoUploader
                photoUrl={user?.profilePhotoUrl}
                initials={initials}
                size={64}
                onUploaded={(updatedUser) => dispatch(setCredentials({ user: updatedUser }))}
                onRemoved={(updatedUser) => dispatch(setCredentials({ user: updatedUser }))}
              />
              <Box>
                <Typography fontWeight={700}>
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.75, mt: 0.5 }}>
                  {user?.isVerified && (
                    <Typography
                      variant="caption"
                      sx={{
                        bgcolor: "success.light",
                        color: "success.main",
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        fontWeight: 600,
                      }}
                    >
                      Verified
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      bgcolor: "info.light",
                      color: "primary.main",
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      fontWeight: 600,
                    }}
                  >
                    Customer
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
                mb: 2,
              }}
            >
              <TextField
                label="First Name"
                value={profile.firstName}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, firstName: e.target.value }))
                }
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ fontSize: 16, color: "text.disabled" }} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <TextField
                label="Last Name"
                value={profile.lastName}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, lastName: e.target.value }))
                }
              />
            </Box>

            <TextField
              fullWidth
              disabled
              label="Email Address"
              value={user?.email || ""}
              sx={{ mb: 2 }}
              helperText="Email cannot be changed"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ fontSize: 16, color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <PhoneNumberField
              fullWidth
              label="Phone Number"
              value={profile.phone}
              onChange={(phone) => setProfile((p) => ({ ...p, phone }))}
              sx={{ mb: 2 }}
            />

            {user?.canManageMultipleTins ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  TINs on this account
                </Typography>
                <Typography variant="body2">
                  {user?.tins?.length > 0
                    ? user.tins
                        .map((t) => (t.label ? `${t.tin} (${t.label})` : t.tin))
                        .join(", ")
                    : "No TINs on file — contact support"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  You manage multiple TINs — pick which one to file each shipment under when creating it.
                </Typography>
              </Box>
            ) : (
            <TextField
              fullWidth
              label="TIN"
              value={profile.tin}
              onChange={(e) =>
                setProfile((p) => ({ ...p, tin: e.target.value }))
              }
              sx={{ mb: 2 }}
              placeholder="e.g. 000-00-000"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
            )}

            <Tooltip title="This status is set by an administrator and cannot be changed here">
              <FormControlLabel
                sx={{
                  mb: 2,
                  ml: 0,
                  bgcolor: "action.hover",
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.5,
                  width: "100%",
                }}
                control={
                  <Checkbox
                    checked={!!user?.isWaypointImportsAgent}
                    disabled
                    icon={<VerifiedUser sx={{ color: "text.disabled" }} />}
                    checkedIcon={<VerifiedUser sx={{ color: "success.main" }} />}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Assigned Waypoint Imports as Agent
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Set by an administrator
                    </Typography>
                  </Box>
                }
              />
            </Tooltip>

            <Divider sx={{ my: 2 }} />
            <Typography
              variant="overline"
              color="text.secondary"
              display="block"
              mb={1.5}
            >
              Delivery Address
            </Typography>

            <TextField
              fullWidth
              label="Street Address"
              value={profile.street}
              onChange={(e) =>
                setProfile((p) => ({ ...p, street: e.target.value }))
              }
              sx={{ mb: 2 }}
              placeholder="e.g. 12 Harbour Drive"
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
                mb: 2.5,
              }}
            >
              <TextField
                label="District"
                value={profile.district}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, district: e.target.value }))
                }
                placeholder="e.g. George Town"
              />
              <TextField
                label="Island"
                value={profile.island}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, island: e.target.value }))
                }
              />
            </Box>

            <Button
              variant="contained"
              fullWidth
              startIcon={<Save />}
              onClick={handleProfileSave}
              disabled={savingProfile}
            >
              {savingProfile ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Save Changes"
              )}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title={
              <Typography fontWeight={700} fontSize={14}>
                Notifications
              </Typography>
            }
            avatar={<Notifications sx={{ color: "text.secondary" }} />}
            sx={{ pb: 1 }}
          />
          <CardContent sx={{ pt: 0 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              display="block"
              mb={2}
            >
              Email Notifications
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
              {[
                {
                  key: "newShipment",
                  label: "New shipment created",
                  sub: "When a shipment is created for you",
                },
                {
                  key: "statusChange",
                  label: "Shipment status change",
                  sub: "When your shipment status changes",
                },
                {
                  key: "payment",
                  label: "Payment received",
                  sub: "When a payment is recorded",
                },
                {
                  key: "overdueAlert",
                  label: "Overdue invoice alerts",
                  sub: "When your invoices are overdue",
                },
                {
                  key: "dailyDigest",
                  label: "Daily summary digest",
                  sub: "Daily overview of your account",
                },
              ].map((n) => (
                <Box
                  key={`email-${n.key}`}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1.25,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {n.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {n.sub}
                    </Typography>
                  </Box>
                  <Switch
                    checked={notificationPreferences.email[n.key]}
                    onChange={(e) =>
                      handleNotificationToggle("email", n.key, e.target.checked)
                    }
                    size="small"
                    disabled={savingNotifications}
                  />
                </Box>
              ))}
            </Box>

            <Typography
              variant="overline"
              color="text.secondary"
              display="block"
              mb={2}
            >
              In-App Notifications
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {[
                {
                  key: "newShipment",
                  label: "New shipment created",
                  sub: "When a shipment is created for you",
                },
                {
                  key: "statusChange",
                  label: "Shipment status change",
                  sub: "When your shipment status changes",
                },
                {
                  key: "payment",
                  label: "Payment received",
                  sub: "When a payment is recorded",
                },
                {
                  key: "overdueAlert",
                  label: "Overdue invoice alerts",
                  sub: "When your invoices are overdue",
                },
                {
                  key: "dailyDigest",
                  label: "Daily summary digest",
                  sub: "Daily overview of your account",
                },
              ].map((n) => (
                <Box
                  key={`inapp-${n.key}`}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1.25,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {n.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {n.sub}
                    </Typography>
                  </Box>
                  <Switch
                    checked={notificationPreferences.inApp[n.key]}
                    onChange={(e) =>
                      handleNotificationToggle("inApp", n.key, e.target.checked)
                    }
                    size="small"
                    disabled={savingNotifications}
                  />
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Card>
            <CardHeader
              title={
                <Typography fontWeight={700} fontSize={14}>
                  Change Password
                </Typography>
              }
              avatar={<Lock sx={{ color: "text.secondary" }} />}
              sx={{ pb: 1 }}
            />
            <CardContent sx={{ pt: 0 }}>
              {pwError && (
                <Alert
                  severity="error"
                  sx={{ mb: 2, borderRadius: 2 }}
                  onClose={() => setPwError("")}
                >
                  {pwError}
                </Alert>
              )}

              {/* A real <form>, not a plain Box — without a form boundary,
                  Chrome's password manager searches the WHOLE page for a
                  "username" field when the saved-password dropdown is used
                  here, and grabs whatever text input it finds first
                  (autoComplete="off" alone doesn't stop this — Chrome
                  deliberately ignores it for login-relevant heuristics).
                  The hidden username input right after <form> gives it a
                  legitimate, in-bounds target instead. */}
              <Box
                component="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  handlePasswordChange();
                }}
              >
                <input
                  type="text"
                  name="username"
                  autoComplete="username"
                  value={user?.email || ""}
                  readOnly
                  style={{ display: "none" }}
                  aria-hidden="true"
                  tabIndex={-1}
                />
                <TextField
                fullWidth
                label="Current Password"
                type={showPw ? "text" : "password"}
                value={passwords.currentPassword}
                onChange={(e) =>
                  setPasswords((p) => ({
                    ...p,
                    currentPassword: e.target.value,
                  }))
                }
                name="currentPassword"
                // "off" rather than "current-password" — that spec value is
                // exactly what tells the browser to autofill the saved
                // login password here, which is the unwanted behavior being
                // fixed; this isn't a login form.
                autoComplete="off"
                sx={{ mb: 2 }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          size="small"
                          onClick={() => setShowPw((v) => !v)}
                          sx={{ minWidth: 0, p: 0.5 }}
                        >
                          {showPw ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </Button>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <TextField
                fullWidth
                label="New Password"
                type={showPw ? "text" : "password"}
                value={passwords.newPassword}
                onChange={(e) =>
                  setPasswords((p) => ({ ...p, newPassword: e.target.value }))
                }
                name="newPassword"
                autoComplete="new-password"
                helperText="Min. 8 characters, one uppercase, one number"
                sx={{ mb: 2 }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          size="small"
                          onClick={() => setShowPw((v) => !v)}
                          sx={{ minWidth: 0, p: 0.5 }}
                        >
                          {showPw ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </Button>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <TextField
                fullWidth
                label="Confirm New Password"
                type={showPw ? "text" : "password"}
                value={passwords.confirmPassword}
                onChange={(e) =>
                  setPasswords((p) => ({
                    ...p,
                    confirmPassword: e.target.value,
                  }))
                }
                name="confirmNewPassword"
                autoComplete="new-password"
                sx={{ mb: 2.5 }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          size="small"
                          onClick={() => setShowPw((v) => !v)}
                          sx={{ minWidth: 0, p: 0.5 }}
                        >
                          {showPw ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </Button>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={
                  savingPw ||
                  !passwords.currentPassword ||
                  !passwords.newPassword
                }
              >
                {savingPw ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  "Change Password"
                )}
              </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Account info */}
          <Card>
            <CardHeader
              title={
                <Typography fontWeight={700} fontSize={14}>
                  Account Information
                </Typography>
              }
              sx={{ pb: 1 }}
            />
            <CardContent sx={{ pt: 0 }}>
              {[
                { label: "Account Type", value: "Customer" },
                {
                  label: "Member Since",
                  value: user?.createdAt
                    ? new Date(user.createdAt).toLocaleDateString("en-US", {
                      month: "long",
                      year: "numeric",
                    })
                    : "—",
                },
                {
                  label: "Email Verified",
                  value: user?.isVerified ? "✅ Verified" : "❌ Not verified",
                },
                {
                  label: "Account Status",
                  value: user?.isActive ? "✅ Active" : "❌ Disabled",
                },
              ].map((row) => (
                <Box
                  key={row.label}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {row.label}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>

          {/* Active Sessions */}
          <Card>
            <CardHeader
              title={
                <Typography fontWeight={700} fontSize={14}>
                  Active Sessions
                </Typography>
              }
              avatar={<Devices sx={{ color: "text.secondary" }} />}
              action={
                sessions.length > 1 && (
                  <Button
                    size="small"
                    color="error"
                    startIcon={<LogoutOutlined fontSize="small" />}
                    onClick={() => setSignOutAllConfirmOpen(true)}
                  >
                    Sign Out All
                  </Button>
                )
              }
              sx={{ pb: 1 }}
            />
            <CardContent sx={{ pt: 0 }}>
              {sessionsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : sessions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No active sessions found.
                </Typography>
              ) : (
                sessions.map((s) => (
                  <Box
                    key={s.sessionId}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      py: 1.25,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      {s.platform === "mobile" ? (
                        <PhoneIphone sx={{ color: "text.disabled" }} />
                      ) : (
                        <Laptop sx={{ color: "text.disabled" }} />
                      )}
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {getSessionLabel(s)}
                          </Typography>
                          {s.isCurrent && (
                            <Chip label="This device" size="small" color="primary" sx={{ height: 18, fontSize: "0.65rem" }} />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Last active {dayjs(s.lastUsedAt).fromNow()}
                        </Typography>
                      </Box>
                    </Box>
                    <Tooltip title={s.isCurrent ? "Sign out this device" : "Sign out this device remotely"}>
                      <IconButton
                        size="small"
                        onClick={() => handleRevokeSession(s)}
                        disabled={revokingId === s.sessionId}
                      >
                        {revokingId === s.sessionId ? (
                          <CircularProgress size={16} />
                        ) : (
                          <LogoutOutlined fontSize="small" color="error" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))
              )}
            </CardContent>
          </Card>

          {/* Danger Zone — self-service account deletion request (Apple
              Guideline 5.1.1(v) parity; web doesn't need it for App Store
              compliance, but customers shouldn't have to email support to
              ask). Deliberately understated relative to the rest of the
              page — this isn't a primary action. */}
          <Card>
            <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Delete Account
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.deletionRequestedAt
                    ? `Requested ${dayjs(user.deletionRequestedAt).format("MMM D, YYYY")} — our team will follow up.`
                    : "Submits a request for our team to review and process."}
                </Typography>
              </Box>
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<DeleteForever fontSize="small" />}
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={!!user?.deletionRequestedAt}
              >
                {user?.deletionRequestedAt ? "Pending" : "Delete Account"}
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Dialog open={deleteConfirmOpen} onClose={() => !requestingDeletion && setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Request account deletion?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This submits a request to permanently delete your account. Our team will review and process it — you'll
            be signed out once submitted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setDeleteConfirmOpen(false)} disabled={requestingDeletion}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleRequestDeletion} disabled={requestingDeletion}>
            {requestingDeletion ? "Submitting…" : "Request Deletion"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={signOutAllConfirmOpen} onClose={() => !revokingAll && setSignOutAllConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Sign out of all devices?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This signs you out everywhere, including this browser — you'll need to log in again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setSignOutAllConfirmOpen(false)} disabled={revokingAll}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleSignOutAll} disabled={revokingAll}>
            {revokingAll ? "Signing out…" : "Sign Out All"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerProfile;
